import { MouseEvent, ReactNode } from 'react';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { Card } from '@components/card';
import { DragHandle } from '@components/drag-and-drop';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AttributeFieldType } from 'api/attributes/requests';

import { AttributeIcon, attributeTextMap } from './common';

type FieldCardProps = {
  attributeType: AttributeFieldType;
  title: string;
  subtitle?: string;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
  rightContent?: ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  onMouseDown?: (event: MouseEvent) => void;
};

const stopPropagation = (event: MouseEvent) => {
  event.stopPropagation();
};

export const FieldCard = ({
  attributeType,
  title,
  subtitle,
  dragAttributes,
  dragListeners,
  rightContent,
  isActive,
  onClick,
  onMouseDown,
}: FieldCardProps) => {
  const isInteractive = Boolean(onClick);
  const finalSubtitle = subtitle ?? attributeTextMap[attributeType];

  return (
    <Card
      spaceX={4}
      spaceY={3}
      variant="default"
      interactive={isInteractive}
      isActive={isActive}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      <Stack orientation="horizontal" align="flex-start" space={2}>
        <IconRow>
          {dragListeners && (
            <DragHandle onClick={stopPropagation} {...dragAttributes} {...dragListeners} />
          )}
          <AttributeIcon type={attributeType} color="elementPurple80" />
        </IconRow>
        <TitleColumn>
          <Text font="semibold14" text={title} />
          <Text font="regular14" text={finalSubtitle} />
        </TitleColumn>
        {rightContent}
      </Stack>
    </Card>
  );
};

const TitleColumn = styled.div({
  flex: 1,
  minWidth: 0,
});

const IconRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  height: theme.base.height,
});
